/**
 * Socket frames in, state-panel props out.
 *
 * A pure reducer rather than more `useState` on an already-long page.
 * The mapping is where this can silently go wrong -- a hop counted from
 * the wrong end, a withheld item that never clears, a bond scale read as
 * the wrong range -- and none of that is visible in a screenshot. Pure
 * means it is testable without a socket, a browser or a render.
 *
 * ## What is real and what is not, stated because the panel cannot say it
 *
 * `server.bond-tick` now carries everything the trust section needs:
 * `bond`, `delta`, `reason`, and as of the frame contract
 * (`docs/02-specs/bond-tick-frame-contract.md`) `rupture_floor`. So the
 * ledger and the ceiling are read from the wire rather than guessed at,
 * and beat 4 is expressible.
 *
 * `repair_progress` and `repair_required` are deliberately **not** read,
 * and are coming off the wire. A countdown to the exit condition lets a
 * visitor optimise against it, and a visitor who is optimising has
 * stopped talking to a character. The ceiling stays: it says she is
 * holding back, and says nothing about what would make her stop.
 *
 * **The trust section fills from a live socket.** Both of the gaps this
 * docstring used to describe have closed, and it said so for long enough
 * afterwards to be worth recording:
 *
 * 1. The emitter exists, at `stage_ws/gateway.py::_bind_bond_sink`.
 * 2. `default_provider.py` now passes `memories` into
 *    `compute_psychology`, so the engine no longer replays an empty
 *    history and returns the baseline every turn.
 *
 * Verified against frames on the wire rather than inferred from either
 * of those: live bond-ticks arrive and render per character.
 *
 * The history is worth keeping because this docstring is the artifact
 * that warns about reading labels, and then became a stale one. An
 * earlier version claimed the ledger was "driven by real frames" on the
 * strength of the frame being *defined*; the version after it claimed
 * the opposite for thirteen commits after the emitter landed. Both were
 * labels that had stopped matching their payload, in a reducer whose
 * whole job is to read payloads.
 *
 * So the detectors, which outlived both mistakes. For any frame a
 * consumer reads, grep for where it is **constructed**, not where it is
 * typed. For any function a pipeline depends on, check what its caller
 * passes, not merely that a caller exists. And for a docstring asserting
 * that something upstream is missing: check that it is still missing
 * before believing it, because a note saying "do not debug this" is
 * exactly the note nobody re-derives.
 *
 * `server.gossip` does have an emitter, in the stage gateway, so the path
 * and the withheld items are real -- behind the appraisal switch, which is
 * off pending metering.
 *
 * **The ceiling is still never inferred.** It is read from
 * `rupture_floor` or it is `null`; nothing derives one from the deltas.
 * `null` is the correct value for an unruptured relationship and the
 * panel renders no constraint block for it, which is why the frame sends
 * `null` rather than `0` -- a zero would render "she will not go past 0"
 * on every relationship that has never been ruptured.
 *
 * A floor of exactly `0.0` is a real floor, the worst rupture the engine
 * can express, and is kept distinct from "no floor". `toCeiling` exists
 * to make that distinction impossible to lose to a truthiness check.
 *
 * **Memory now has a frame.** `server.recall` carries what she recalled,
 * so the section fills from the wire rather than from the transcript,
 * which would have been a different claim wearing the same words. What
 * this docstring said before, that memory had no frame at all, was true
 * for as long as it stood; the note above about labels that stop
 * matching their payload is why it is rewritten here rather than left
 * for someone to re-derive.
 *
 * The frame carries a null this reducer must not flatten. `recalled ===
 * null` means retrieval did not run; `[]` means it ran and matched
 * nothing. `PanelState.memory` keeps both facts, plus `remembered`,
 * because the three empty states the panel has to tell apart are exactly
 * those two crossed, and a bare `MemoryTrace[]` can hold neither.
 */

import type {
  GossipPath,
  MemoryRecall,
  TrustState,
  WithheldNote,
} from "@/components/rehearsal/StatePanel";
import { statePanelCopy } from "@/components/rehearsal/StatePanel.copy";
import { tierFor } from "@/rehearsal/retention";
import type { StageMessage } from "@/api/types";

export interface PanelState {
  readonly trust: readonly TrustState[];
  /**
   * What each character recalled, keyed by whose memory it was.
   *
   * Keyed rather than flat for the reason `pathsBy` below is keyed, and
   * the mistake was available here to make again: the section is headed
   * "what **she** still holds", so one list shared by the room would show
   * a visitor Ellis's memories under Margo's name the moment a second
   * character is brought in. Gossip shipped that bug and it was found by
   * driving the arc rather than by a test; there was no reason to ship it
   * a second time one field over.
   *
   * A missing key is not an empty recall. It means that character has
   * sent none, which `viewFor` surfaces as `null`.
   */
  readonly memoryBy: Readonly<Record<string, MemoryRecall>>;
  /**
   * What each character knows, keyed by who was told.
   *
   * One flat list was right while the room held one person. The moment a
   * visitor can bring someone in, it conflates: the panel is headed "how
   * **she** knows" and was showing Margo's items and Ellis's together, so
   * something the visitor had told Margo directly came back as **"Margo
   * told her"** — the hub relabels a message's source to its own identity
   * when it passes one on, which is harmless until two listeners share a
   * list.
   *
   * Found by driving the arc after beat 1 became reachable. It was not
   * wrong before beat 1 existed, which is why nothing caught it.
   */
  readonly pathsBy: Readonly<Record<string, readonly GossipPath[]>>;
  readonly withheldBy: Readonly<Record<string, readonly WithheldNote[]>>;
  /**
   * Paths and withheld notes older than `MAX_ITEMS_SHOWN`, kept up to
   * `MAX_ITEMS_KEPT` so #19's "rest of it" disclosure has rows to reveal
   * rather than a bare count. Same keying as `pathsBy` / `withheldBy`.
   */
  readonly olderPathsBy: Readonly<Record<string, readonly GossipPath[]>>;
  readonly olderWithheldBy: Readonly<Record<string, readonly WithheldNote[]>>;
}

/**
 * One listener's view, for the panel.
 *
 * `withheldElsewhere` names the other characters who are holding
 * something, and exists for the case where this one is not. The panel is
 * the addressee's view -- "how *she* knows" is singular, and showing the
 * room would mean showing a visitor things the character they are
 * speaking to does not know, which is the failure this whole section was
 * just repaired for. So the blank stays blank, and says why.
 *
 * That is not a leak. The only way a character comes to be holding
 * something is that the visitor told them, so the visitor is the source
 * of every name this can return and cannot be surprised by one. It names
 * who, never what.
 */
export function viewFor(
  state: PanelState,
  agentId: string | undefined,
  nameFor?: (voiceId: string) => string,
): {
  paths: readonly GossipPath[];
  olderPaths: readonly GossipPath[];
  withheld: readonly WithheldNote[];
  olderWithheld: readonly WithheldNote[];
  withheldElsewhere: readonly string[];
  memory: MemoryRecall | null;
} {
  if (!agentId)
    return {
      paths: [],
      olderPaths: [],
      withheld: [],
      olderWithheld: [],
      withheldElsewhere: [],
      memory: null,
    };
  const withheld = state.withheldBy[agentId] ?? [];
  return {
    paths: state.pathsBy[agentId] ?? [],
    olderPaths: state.olderPathsBy[agentId] ?? [],
    withheld,
    olderWithheld: state.olderWithheldBy[agentId] ?? [],
    // `?? null` rather than `?? emptyRecall`. No key means this
    // character has never reported a recall, which the panel renders as
    // a blank that claims nothing -- not as "she looked and found
    // nothing", which is a claim and would be unfounded.
    memory: state.memoryBy[agentId] ?? null,
    // Only computed when this listener has nothing, because that is the
    // only case the panel renders it in. A visitor reading a full
    // section does not need to be told about a second one.
    withheldElsewhere:
      withheld.length > 0
        ? []
        : Object.entries(state.withheldBy)
            .filter(([id, notes]) => id !== agentId && notes.length > 0)
            .map(([id]) => (nameFor ? nameFor(id) : id)),
  };
}

export const emptyPanelState: PanelState = {
  trust: [],
  // Empty map, so every character resolves to `null` rather than to an
  // empty recall. A fresh panel has not been told anything about memory,
  // and an empty recall would be it claiming she recalled nothing --
  // which is the sentence this section was rebuilt to stop showing to
  // the people it was not true of.
  memoryBy: {},
  pathsBy: {},
  withheldBy: {},
  olderPathsBy: {},
  olderWithheldBy: {},
};

/** How many ledger rows one relationship shows by default. */
const MAX_ENTRIES_SHOWN = 4;
/**
 * How many ledger rows one relationship keeps in total, shown plus the
 * ones behind #19's "rest of it" disclosure. A ceiling rather than
 * unbounded growth, for the same reason `MAX_ENTRIES_SHOWN` was: a take
 * long enough to need it is also long enough that "everything, forever"
 * would be its own kind of unreadable, just one tap further in.
 */
const MAX_ENTRIES_KEPT = 20;
/** How many items each gossip section shows by default. */
const MAX_ITEMS_SHOWN = 4;
/** How many items each gossip section keeps in total. See `MAX_ENTRIES_KEPT`. */
const MAX_ITEMS_KEPT = 20;

/**
 * Split a bounded history into what is shown and what is older.
 *
 * `full` is assumed already capped at the "kept" limit by the caller --
 * this only decides the shown/older boundary within it, so the two
 * halves always recombine to exactly `full`.
 */
function splitShown<T>(full: readonly T[], shownCount: number): {
  shown: readonly T[];
  older: readonly T[];
} {
  const shown = full.slice(-shownCount);
  return { shown, older: full.slice(0, full.length - shown.length) };
}

/**
 * `bond` arrives 0-1; the panel speaks in 0-100.
 *
 * Guarded rather than multiplied blind: a provider that starts sending
 * 0-100 would render 4400 and look like a bug in the panel rather than a
 * change on the wire.
 */
function toLevel(bond: number): number {
  if (!Number.isFinite(bond)) return 0;
  const scaled = bond <= 1 ? bond * 100 : bond;
  return Math.max(0, Math.min(100, scaled));
}

/**
 * The ceiling, on the panel's scale, or `null` when there is none.
 *
 * Separate from `toLevel` because the two failure modes differ. A missing
 * `bond` is a malformed frame; a missing `rupture_floor` is the ordinary
 * case and means the relationship is unruptured.
 *
 * A floor of exactly `0.0` is a **real** floor -- the worst rupture the
 * engine can express -- so this cannot be a truthiness check. `if
 * (!floor) return null` reads correctly, passes every test written with
 * a nonzero fixture, and silently erases the most severe state in the
 * system.
 */
function toCeiling(floor: number | null | undefined): number | null {
  if (floor === null || floor === undefined) return null;
  if (!Number.isFinite(floor)) return null;
  return toLevel(floor);
}

function applyBond(
  state: PanelState,
  frame: Extract<StageMessage, { kind: "server.bond-tick" }>,
  nameFor: (voiceId: string) => string,
): PanelState {
  const existing = state.trust.find((t) => t.voiceId === frame.voice_id);
  const entry = {
    id: frame.id,
    // No reason means the engine moved trust without saying why. Shown
    // as such rather than hidden: a ledger row with no cause is a real
    // thing to notice, and dropping it would leave the balance moving
    // for reasons the panel claims not to have.
    reason: frame.reason ?? statePanelCopy.trust.noReason,
    delta: Math.round(frame.delta <= 1 && frame.delta >= -1 ? frame.delta * 100 : frame.delta),
  };
  // A turn that moved nothing gets no ledger row, but still updates the
  // level and the ceiling.
  //
  // The frame contract asks emitters not to send these (§5) and this is
  // the same rule enforced where it can be tested. The cost is not
  // cosmetic: the ledger shows only the last four rows by default, so a
  // run of no-op turns would push the rupture out of the shown window
  // and behind #19's disclosure while the number stays wrong. Caught by
  // rendering the beat and finding "you arrived  0" sitting in the slot
  // the rupture needed.
  const moved = Math.round(entry.delta) !== 0;
  // Full history, reassembled from the two halves the state already
  // carries so nothing older than `MAX_ENTRIES_KEPT` needs to be stored
  // twice. `moved` gates a new row exactly as it always did; a no-op
  // turn re-derives the same full history unchanged.
  const priorFull = existing
    ? [...existing.olderEntries, ...existing.entries]
    : [];
  const full = (moved ? [...priorFull, entry] : priorFull).slice(-MAX_ENTRIES_KEPT);
  const { shown, older } = splitShown(full, MAX_ENTRIES_SHOWN);
  const next: TrustState = {
    voiceId: frame.voice_id,
    name: nameFor(frame.voice_id),
    level: toLevel(frame.bond),
    entries: shown,
    olderEntries: older,
    // Read from the frame now, not inferred and not carried forward.
    //
    // Carried forward would be worse than absent: a relationship that
    // ruptures and then genuinely repairs would keep rendering the old
    // ceiling forever, so the panel would say she will not go past a
    // number she has already passed. `?? null` rather than `?? existing`
    // for exactly that reason — the frame is the current truth and a
    // missing floor means there is no longer one.
    ceiling: toCeiling(frame.rupture_floor),
  };
  return {
    ...state,
    trust: existing
      ? state.trust.map((t) => (t.voiceId === frame.voice_id ? next : t))
      : [...state.trust, next],
  };
}

/**
 * A display name for whoever passed something on.
 *
 * `told_by` is the **hub's agent id**, not a voice id:
 * `stage_tak_rehearsal_rhs_01M18APY0Y22AR2VE7GDTVPAP7_ellis`. Passed
 * straight to `nameFor` it does not match, falls through unchanged, and
 * the panel renders
 *
 *   "stage_tak_rehearsal_rhs_01M18APY…_ellis told her. once removed."
 *
 * — a fifty-character internal id inside the one sentence that carries
 * the demo's whole argument. Caught by driving beat 3 rather than by any
 * test, and it is the second time this field has leaked an id: it read
 * "ellis told her" in lower case before, which was the same defect with a
 * smaller blast radius.
 *
 * The character id is the trailing segment, so this tries the whole
 * string first and then that segment, and gives back the raw value only
 * when neither resolves — never a partial id dressed up as a name.
 */
function resolveSpeaker(
  toldBy: string,
  nameFor: (voiceId: string) => string,
): string {
  const whole = nameFor(toldBy);
  if (whole !== toldBy) return whole;

  const tail = toldBy.slice(toldBy.lastIndexOf("_") + 1);
  if (tail && tail !== toldBy) {
    const resolved = nameFor(tail);
    if (resolved !== tail) return resolved;
    // A bare character id reads better than the full agent id even
    // unresolved: "ellis" over "stage_tak_rehearsal_rhs_…_ellis".
    return tail;
  }
  return toldBy;
}

/** The character id at the end of a hub or stage agent id. */
export function toVoiceId(agentOrVoiceId: string): string {
  const tail = agentOrVoiceId.slice(agentOrVoiceId.lastIndexOf("_") + 1);
  return tail || agentOrVoiceId;
}

function applyGossip(
  state: PanelState,
  frame: Extract<StageMessage, { kind: "server.gossip" }>,
  nameFor: (voiceId: string) => string,
): PanelState {
  /*
    Whose view this belongs to, normalised to a voice id.

    `to_agent` arrives in two shapes: the hub's own identity for something
    it heard (`margo`), and a full stage agent id for something it passed
    on (`stage_tak_rehearsal_rhs_…_ellis`). Both end in the character id,
    so both normalise to the key the page already has — the voice it is
    addressing — without the page reconstructing an agent id from a take
    id, which would be a second place to get the format wrong.
  */
  const listener = toVoiceId(frame.to_agent ?? "");
  if (!listener) return state;

  if (frame.event === "withheld") {
    // Dedup against the FULL kept history, not just the shown slice --
    // an item that fell behind the disclosure and is withheld again must
    // still be recognised as the same item rather than doubled.
    const priorFull = [
      ...(state.olderWithheldBy[listener] ?? []),
      ...(state.withheldBy[listener] ?? []),
    ];
    const rest = priorFull.filter((w) => w.content !== frame.content);
    const full = [
      ...rest,
      {
        id: frame.id,
        content: frame.content,
        reason: frame.reason ?? statePanelCopy.withheld.noReason,
      },
    ].slice(-MAX_ITEMS_KEPT);
    const { shown, older } = splitShown(full, MAX_ITEMS_SHOWN);
    return {
      ...state,
      withheldBy: { ...state.withheldBy, [listener]: shown },
      olderWithheldBy: { ...state.olderWithheldBy, [listener]: older },
    };
  }

  const chain = frame.chain ?? [];
  const priorFull = [
    ...(state.olderPathsBy[listener] ?? []),
    ...(state.pathsBy[listener] ?? []),
  ];
  const rest = priorFull.filter((p) => p.content !== frame.content);
  const full = [
    ...rest,
    {
      id: frame.id,
      content: frame.content,
      // The speaker who handed it over, which is the second-to-last
      // hop, not the last. The last is whoever holds it now, and
      // reading that would make every item claim it was told by its
      // current holder.
      speaker: frame.told_by ? resolveSpeaker(frame.told_by, nameFor) : null,
      hops: frame.hops ?? Math.max(0, chain.length - 1),
      confidences: chain.map((hop) => hop.confidence),
    },
  ].slice(-MAX_ITEMS_KEPT);
  const { shown, older } = splitShown(full, MAX_ITEMS_SHOWN);
  return {
    ...state,
    pathsBy: { ...state.pathsBy, [listener]: shown },
    olderPathsBy: { ...state.olderPathsBy, [listener]: older },
  };
}

/**
 * What she recalled this turn, replacing what she recalled last turn.
 *
 * Replaces rather than accumulates, which is the opposite of every other
 * section here and is deliberate. The trust ledger and the gossip lists
 * are histories: they answer "what has happened between you". This one
 * answers "what came back to her when you said that", and a recall from
 * three turns ago is not part of the answer. Accumulating would fill the
 * column with everything she has ever surfaced and quietly turn the
 * heading, "what she still holds", into "what she has ever held" -- and
 * it would do so in a way that looks like the feature working.
 *
 * That also means the section can legitimately go from full back to
 * empty, which is why the empty states had to be told apart before this
 * function could be written at all. Emptying into "she has nothing of
 * yours to hold" would have made a working recall look like a broken one
 * every time the visitor changed the subject.
 *
 * `MAX_ITEMS_SHOWN` still applies. The SDK retrieves at most five per kind
 * across six kinds, so an unbounded render could put thirty lines into a
 * column sized for four. Unlike the ledger and the gossip sections, this
 * replaces rather than accumulates (see above), so there is no older
 * history to keep for #19's disclosure -- a recall from three turns ago
 * is not "the rest of" this turn's recall, it is a different turn's.
 */
function applyRecall(
  state: PanelState,
  frame: Extract<StageMessage, { kind: "server.recall" }>,
): PanelState {
  const voiceId = toVoiceId(frame.voice_id ?? "");
  if (!voiceId) return state;

  // `undefined` and `null` are the same fact here -- the frame did not
  // say what she recalled -- and both must stay distinct from `[]`.
  // Written as an explicit comparison rather than `?? []`, which would
  // silently convert "not asked" into "asked, found nothing" and put the
  // wrong one of three sentences on the screen.
  const recalled = frame.recalled ?? null;

  return {
    ...state,
    memoryBy: {
      ...state.memoryBy,
      [voiceId]: {
        traces:
          recalled === null
            ? null
            : recalled.slice(-MAX_ITEMS_SHOWN).map((ref) => ({
                id: ref.id,
                // The preview, capped at 140 characters upstream. The
                // whole record is deliberately not on the wire.
                text: ref.preview ?? "",
                tier: tierFor(ref.retention),
              })),
        remembered: frame.remembered === true,
      },
    },
  };
}

/**
 * Fold one frame into the panel's state.
 *
 * Returns the same object when the frame is not the panel's, so a caller
 * can use identity to skip a re-render.
 */
export function applyPanelFrame(
  state: PanelState,
  frame: StageMessage,
  nameFor: (voiceId: string) => string = (id) => id,
): PanelState {
  if (frame.kind === "server.bond-tick") return applyBond(state, frame, nameFor);
  if (frame.kind === "server.gossip") return applyGossip(state, frame, nameFor);
  if (frame.kind === "server.recall") return applyRecall(state, frame);
  return state;
}
